import { keys, isGameInputBlocked } from '../input.js';
import { findNearestPickup, collectPickup } from '../world/pickups.js';
import { isPlayerNearPod } from '../world/landingPod.js';
import { getNearbySocket, fillSocket } from '../world/ruins.js';
import { isPlayerAtPondEdge, canCastLure, castLure } from '../world/pond.js';
import { throwItem } from '../world/throwing.js';
import { getBoardCarrierInReach, takeBoardFromCarrier } from '../world/creatures.js';
import { player, startThrow } from './controller.js';
import { addItem, hasItem, removeItem } from '../game/inventory.js';
import { canRepair, tryRepair } from '../game/communicator.js';
import { showPrompt, hidePrompt } from '../ui/prompt.js';

// Edge-triggered E / F presses: we only act on the transition, not while held.
let wasEDown = false;
let wasFDown = false;

// Crystal preference order when throwing — first one the player has wins.
// Name is preserved on the thrown item so the ground pickup is identical
// to the original in the inventory.
const CRYSTAL_ORDER = [
  { id: 'crystal_amber', name: 'Amber Crystal', color: 0xffcf50 },
  { id: 'crystal_azure', name: 'Azure Crystal', color: 0x3ae0ff },
  { id: 'crystal_rose',  name: 'Rose Crystal',  color: 0xff5aa0 },
];

export function updateInteractions(character) {
  // While input is blocked (e.g. dialog), don't show prompts or collect.
  if (isGameInputBlocked()) {
    hidePrompt();
    wasEDown = false;
    wasFDown = false;
    return;
  }

  // ---- Throw a crystal with F ----
  // Block repeat throws while the animation is still playing so the arm
  // isn't snapped back mid-windup.
  const fDown = keys.has('f');
  if (fDown && !wasFDown && player.throwPhase === 0) {
    const chosen = CRYSTAL_ORDER.find((c) => hasItem(c.id));
    if (chosen) {
      throwItem({
        id:          chosen.id,
        name:        chosen.name,
        color:       chosen.color,
        charPos:     player.charPos,
        charForward: player.charForward,
      });
      removeItem(chosen.id);
      startThrow();
    }
  }
  wasFDown = fDown;

  const eDown = keys.has('e');

  // Repair-at-pod takes priority over pickups when all parts are held and
  // the player is standing next to the pod.
  const canRepairHere = canRepair() && isPlayerNearPod(character.position);
  if (canRepairHere) {
    showPrompt({ key: 'E', text: 'Repair Communicator' });
    if (eDown && !wasEDown) tryRepair();
    wasEDown = eDown;
    return;
  }

  // Ruin socket — offer to place the matching crystal if the player has
  // one. No prompt if they don't; the socket's coloured glow is the hint.
  const socket = getNearbySocket(character.position);
  if (socket && hasItem(socket.requiredId)) {
    showPrompt({ key: 'E', text: `Place ${socket.crystalName}` });
    if (eDown && !wasEDown) {
      if (fillSocket(socket.id)) removeItem(socket.requiredId);
    }
    wasEDown = eDown;
    return;
  }

  // Pond lure — if the player is holding a Lure Bloom and standing at the
  // pond's edge, offer to cast it. Consumed on use; moves the raft.
  if (canCastLure() && hasItem('lure_bloom') && isPlayerAtPondEdge(character.position)) {
    showPrompt({ key: 'E', text: 'Cast Lure Bloom' });
    if (eDown && !wasEDown) {
      if (castLure(character.position)) removeItem('lure_bloom');
    }
    wasEDown = eDown;
    return;
  }

  // Board carrier — if we're next to the lured/eating creature with the
  // glowing panel, offer to take the Circuit Board off its back.
  const carrier = getBoardCarrierInReach(character.position);
  if (carrier) {
    showPrompt({ key: 'E', text: 'Take Circuit Board' });
    if (eDown && !wasEDown && takeBoardFromCarrier()) {
      addItem({
        id:    'comm_board',
        name:  'Circuit Board',
        color: 0x4cd896,
      });
    }
    wasEDown = eDown;
    return;
  }

  const nearby = findNearestPickup(character.position);
  if (nearby) showPrompt({ key: 'E', text: `Pick up ${nearby.name}` });
  else        hidePrompt();

  if (eDown && !wasEDown && nearby) {
    collectPickup(nearby);
    addItem({
      id:    nearby.id,
      name:  nearby.name,
      color: nearby.color,
      icon:  nearby.icon,
    });
  }
  wasEDown = eDown;
}
